import React, {ReactNode, RefObject, useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {useNavigate} from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {ActionBar} from '../../../shared/components/record/RecordKit';
import {InvoiceDto} from '../../invoice/dtos/invoice.dto';
import {setInvoiceFollowUpsStopped} from '../../invoice/invoice.service';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {money} from './record-math';
import {SimpleDialog, TotalRow} from './EstimateFooter';
import {
  formatAmountPaidInput, formatDueDate, formatFollowUpDate, formatPaymentDate, fullPaymentRejection, invoiceDueDate,
  invoiceRemainingBalance, isPastDue, parseAmountPaid, paymentAmountRejection, paymentStanding, upcomingFollowUps,
} from './invoice-math';
import './InvoiceRecord.css';

/*
 * The invoice's parts of the record (SQ-598), after Android's
 * InvoicePaymentSection.kt, the standing lines in EstimateFooterView.kt and
 * ClientDetailScreen.kt's InvoiceActionButtonRow: Add Payment / Go to Send
 * at the top, where the payments stand under the Job Total, and the
 * Add Payment section with the send block at the foot.
 */

/**
 * InvoiceActionButtonRow: two green pills that only move the page, each with a
 * small down chevron. Hidden: the box at its full size with nothing in it,
 * while the page does not yet know it is an invoice.
 */
export const InvoiceActionBar = ({onScrollToPayment, onScrollToSend, hidden, preview}: {
  onScrollToPayment: () => void; onScrollToSend: () => void; hidden?: boolean;
  /** Preview (SQ-337): the customer's copy of the invoice; while it shows, the bar holds only Admin View. */
  preview?: {on: boolean; onToggle: () => void};
}) => (
  <ActionBar>
    <div className={hidden ? 'er-action-row iv-bar--hidden' : 'er-action-row'} aria-hidden={hidden || undefined}>
      {preview && !hidden && (
        <button type="button" className={preview.on ? 'er-bar-button er-bar-button--solid' : 'er-bar-button er-bar-button--green'}
                onClick={preview.onToggle}>
          <VisibilityIcon/>{preview.on ? 'Admin View' : 'Preview'}
        </button>
      )}
      {/* The payment section's own button is "Add Payment" too: this one's name says where it goes (SQ-716). */}
      {!preview?.on && ([
        ['Add Payment', 'Add Payment, go to the payment section', onScrollToPayment],
        ['Go to Send', undefined, onScrollToSend],
      ] as const).map(([label, name, onClick]) => (
        <button key={label} type="button" className="er-bar-button er-bar-button--green iv-bar-button" tabIndex={hidden ? -1 : undefined}
                aria-label={name} onClick={hidden ? undefined : onClick}>
          {label}
          <KeyboardArrowDownIcon className="iv-bar-chevron" aria-hidden="true"/>
        </button>
      ))}
    </div>
  </ActionBar>
);

/**
 * Under the Job Total: exactly one of Overpaid, Amount Due $0.00, or Amount
 * Paid and Remaining Balance; then, while anything is owed, the due date.
 */
export const InvoiceStandingLines = ({invoice, total}: {invoice: InvoiceDto; total: number}) => {
  const standing = paymentStanding(total, invoice.amountPaid ?? 0, invoice.status);
  if (standing.overpaid > 0) return <TotalRow label="Overpaid" value={money(standing.overpaid)} amount={standing.overpaid}/>;
  if (standing.paidInFull) return <TotalRow label="Amount Due" value={money(0)} amount={0}/>;
  // SQ-687: the date stamped at the first send; past it, the line says so in red.
  const due = invoiceDueDate(invoice.dueAt);
  const overdue = !!due && isPastDue(due);
  return <>
    {standing.paid > 0 && <>
      <TotalRow label="Amount Paid" value={money(standing.paid)} amount={standing.paid}/>
      <TotalRow label="Remaining Balance" value={money(standing.remaining)} amount={standing.remaining}/>
    </>}
    {due && (
      <TotalRow label={overdue ? 'Overdue Since' : 'Due Date'}
                value={<span className={overdue ? 'iv-overdue' : undefined}>{formatDueDate(due)}</span>}/>
    )}
  </>;
};

/** The pop-up when an edit or a payment moves the invoice between Paid and not: the notice and OK. */
export const InvoiceNoticeDialog = ({text, onClose}: {text: string; onClose: () => void}) => (
  <Dialog open onClose={onClose} maxWidth="xs">
    <DialogContent><DialogContentText>{text}</DialogContentText></DialogContent>
    <DialogActions><Button variant="contained" onClick={onClose}>OK</Button></DialogActions>
  </Dialog>
);

const PaymentLine = ({label, value, strong}: {label: string; value: string; strong?: boolean}) => (
  <div className={strong ? 'iv-pay-line iv-pay-line--strong' : 'iv-pay-line'} role="group" aria-label={label}>
    <span>{label}</span><span>{value}</span>
  </div>
);

/** "Payment Amount" with its dollar box beside it; empty, the amount shows as a grey placeholder. */
const PaymentAmountField = ({value, placeholder, onChange}: {value: string; placeholder: string; onChange: (text: string) => void}) => (
  <label className="iv-pay-amount">
    <span>Payment Amount</span>
    <span className="iv-money-box">
      <span className="iv-money-sign">$</span>
      <input type="text" inputMode="decimal" value={value} placeholder={placeholder} aria-label="Payment Amount"
             onChange={e => onChange(e.target.value)}/>
    </span>
  </label>
);

type PaymentKind = 'full' | 'partial';

/**
 * Add Payment and the send block (InvoicePaymentAndSendSection). Add Payment
 * keeps its label whatever the status. Open, it lists the payments on file
 * and, while anything is owed, asks Paid in Full (what is owed, editable
 * upward: more is an overpayment) or Partial Payment (typed, with the balance
 * it leaves); Record Payment files it as one more payment and folds the
 * section. Folded, what has been paid shows under the button.
 */
export const InvoicePaymentSection = ({invoice, total, working, onRecordPayment, paymentRef, sendRef, sendBlock}: {
  invoice: InvoiceDto; total: number; working: boolean; onRecordPayment: (amount: number) => void;
  paymentRef: RefObject<HTMLDivElement>; sendRef: RefObject<HTMLDivElement>; sendBlock: ReactNode;
}) => {
  const paid = invoice.amountPaid ?? 0;
  const standing = paymentStanding(total, paid, invoice.status);
  const payments = invoice.payments ?? [];
  const [expanded, setExpanded] = useState(false);
  // A recorded payment starts the next one afresh: no choice made, Partial empty, Paid in Full at what is owed.
  const [kind, setKind] = useState<PaymentKind | null>(null);
  const [partialText, setPartialText] = useState('');
  const [fullText, setFullText] = useState(formatAmountPaidInput(standing.remaining));
  useEffect(() => {
    setKind(null);
    setPartialText('');
  }, [invoice.id, invoice.amountPaid]);
  useEffect(() => {
    setFullText(formatAmountPaidInput(invoiceRemainingBalance(total, invoice.amountPaid ?? 0)));
  }, [invoice.id, invoice.amountPaid, total]);

  const partialAmount = parseAmountPaid(partialText);
  const partialError = paymentAmountRejection(partialText, standing.remaining);
  const fullAmount = parseAmountPaid(fullText);
  const fullError = fullPaymentRejection(fullText, standing.remaining);
  const amountToRecord = kind === 'full' ? (fullError === null ? fullAmount : null)
    : kind === 'partial' ? (partialError === null ? partialAmount : null) : null;
  const fullOver = (fullAmount ?? 0) - standing.remaining;

  const summary = (strong: boolean) => <>
    <PaymentLine strong={strong} label="Amount Paid" value={money(standing.paid)}/>
    {!standing.paidInFull && <PaymentLine strong={strong} label="Remaining Balance" value={money(standing.remaining)}/>}
    {standing.overpaid > 0 && <PaymentLine strong={strong} label="Overpaid" value={money(standing.overpaid)}/>}
  </>;

  return (
    <div className="er-info iv-payment" ref={paymentRef} role="group" aria-label="Payment">
      <button type="button" className="er-tonal er-tonal--icon" disabled={working} aria-expanded={expanded} onClick={() => setExpanded(e => !e)}>
        Add Payment {expanded ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
      </button>

      {!expanded && standing.paid > 0 && <div className="iv-pay-lines">{summary(false)}</div>}

      {expanded && <>
        {payments.length > 0 && (
          <div className="iv-pay-lines">
            <div className="iv-pay-caption">Payments recorded</div>
            {payments.map(p => <PaymentLine key={p.id} label={p.createdAt ? formatPaymentDate(p.createdAt) : 'Payment'} value={money(p.amount)}/>)}
            {summary(true)}
          </div>
        )}
        {standing.paidInFull ? <div className="iv-pay-full">Paid in full</div> : (
          <div className="iv-pay-form">
            {payments.length > 0 && <div className="iv-pay-caption">Add another payment</div>}
            <div className="iv-pay-kinds" role="radiogroup" aria-label="Payment kind">
              {([['full', 'Paid in Full'], ['partial', 'Partial Payment']] as const).map(([value, label]) => (
                <label key={value} className="iv-radio">
                  <input type="radio" name={`payment-kind-${invoice.id}`} checked={kind === value} onChange={() => setKind(value)}/>
                  {label}
                </label>
              ))}
            </div>
            {kind === 'full' && <>
              <PaymentAmountField value={fullText} placeholder={formatAmountPaidInput(standing.remaining)} onChange={setFullText}/>
              {fullOver >= 0.005
                ? <PaymentLine strong label="Overpaid" value={money(fullOver)}/>
                : <PaymentLine strong label="Remaining Balance" value={money(invoiceRemainingBalance(standing.remaining, fullAmount ?? 0))}/>}
              {fullError && <div className="er-small er-error">{fullError}</div>}
            </>}
            {kind === 'partial' && <>
              <PaymentAmountField value={partialText} placeholder={formatAmountPaidInput(0)} onChange={setPartialText}/>
              <PaymentLine strong label="Remaining Balance" value={money(invoiceRemainingBalance(standing.remaining, partialAmount ?? 0))}/>
              {/* Nothing to say about an empty box or a zero; a wrong amount says why. */}
              {partialError && partialText.trim() && partialAmount !== 0 && <div className="er-small er-error">{partialError}</div>}
            </>}
            <button type="button" className="er-filled iv-record" disabled={working || amountToRecord === null || amountToRecord <= 0}
                    onClick={() => {
                      if (amountToRecord === null) return;
                      onRecordPayment(amountToRecord);
                      setExpanded(false);
                    }}>
              Record Payment
            </button>
          </div>
        )}
      </>}

      <div className="iv-send-rule" ref={sendRef}/>
      {sendBlock}
    </div>
  );
};

/**
 * Send Invoice (filled, tonal once paid) and Send Receipt (filled once anything is paid), then "Receipt sent".
 * SQ-745: a receipt can go out before the invoice; then, until the invoice is sent, Send Invoice says so.
 */
export const InvoiceSendButtons = ({invoice, emails, phones, working, onSendInvoice, onSendReceipt}: {
  invoice: InvoiceDto; emails: string[]; phones: string[]; working: boolean;
  onSendInvoice: (emails: string[], phones: string[]) => void; onSendReceipt: (emails: string[], phones: string[]) => void;
}) => {
  const hasRecipients = emails.length > 0 || phones.length > 0;
  const isPaid = invoice.status === 'Paid';
  // SQ-429: a receipt goes out for any payment, a part payment too.
  const hasPayment = isPaid || (invoice.amountPaid ?? 0) > 0;
  return (
    <div className="iv-send-buttons">
      <button type="button" className={isPaid ? 'er-tonal' : 'er-filled'} disabled={working || !hasRecipients}
              onClick={() => onSendInvoice(emails, phones)}>Send Invoice</button>
      {invoice.receiptSentAt && invoice.status === 'Created' && <div className="iv-invoice-unsent">Invoice not sent yet</div>}
      <button type="button" className={hasPayment ? 'er-filled' : 'er-tonal'} disabled={working || !hasPayment || !hasRecipients}
              onClick={() => onSendReceipt(emails, phones)}>Send Receipt</button>
      {invoice.receiptSentAt && <div className="iv-receipt-sent">Receipt sent</div>}
    </div>
  );
};

const FOLLOW_UPS_OFF_LINE = 'Invoice follow-ups are turned off, so none go out on any invoice.';

/**
 * SQ-688: under Send Invoice / Send Receipt, while follow-up emails are still
 * to come on an unpaid, sent invoice: when they go out, and Stop (asked
 * first). Once stopped, a line saying so and Turn back on (the ones still to
 * come go out again). Unlike an estimate's, sending again does not restart
 * them: they are counted from the due date, which a resend never moves.
 */
export const InvoiceFollowUpsLine = ({invoice, owesMoney, canChange, working, onStoppedChange}: {
  invoice: InvoiceDto; owesMoney: boolean;
  /** Holds invoices.send: who may send the invoice may stop its follow-ups. */
  canChange: boolean; working: boolean;
  /** The invoice's new followUpsStoppedAt, as the server answered it. */
  onStoppedChange: (stoppedAt: string | null) => void;
}) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setError(undefined); }, [invoice.id]);

  const upcoming = upcomingFollowUps(invoice.dueAt, invoice.followUpDays);
  // Follow-ups only go out on a sent invoice (the server checks the same).
  const sent = invoice.status === 'Sent' || invoice.status === 'Overdue';
  if (!sent || !owesMoney || upcoming.length === 0) return null;
  const stopped = !!invoice.followUpsStoppedAt;
  // The company's switch silences every invoice's, whatever this one booked;
  // it is turned back on from Due dates and follow-ups, not here.
  if (invoice.companyFollowUpsEnabled === false) {
    return (
      <div className="iv-follow-ups" role="group" aria-label="Follow-up emails">
        <div className="iv-follow-ups-text">{FOLLOW_UPS_OFF_LINE}</div>
      </div>
    );
  }

  const change = async (stop: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const answer = await setInvoiceFollowUpsStopped(navigate, invoice.id, stop);
      onStoppedChange(answer.stoppedAt ?? null);
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError(stop ? 'Error stopping invoice follow-ups' : 'Error resuming invoice follow-ups', {invoiceId: invoice.id}, e as Error);
      setError(stop ? "Couldn't stop the follow-ups. Try again." : "Couldn't turn the follow-ups back on. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="iv-follow-ups" role="group" aria-label="Follow-up emails">
      <div className="iv-follow-ups-text">
        {stopped
          ? 'Follow-up emails are stopped on this invoice.'
          : `Follow-up emails go out ${upcoming.map(formatFollowUpDate).join(', ')} while a balance is owed.`}
      </div>
      {canChange && (
        <button type="button" className="er-tonal er-tonal--compact" disabled={working || busy}
                onClick={() => (stopped ? void change(false) : setConfirm(true))}>
          {stopped ? 'Turn follow-ups back on' : 'Stop follow-up emails'}
        </button>
      )}
      {error && <div className="er-small er-error">{error}</div>}
      {confirm && (
        <SimpleDialog title="Stop follow-ups on this invoice?"
                      text="No more follow-up emails will go out on this invoice until you turn them back on."
                      confirm="Stop follow-ups" cancel="Keep them" danger
                      onConfirm={() => void change(true)} onClose={() => setConfirm(false)}/>
      )}
    </div>
  );
};
